<template>
  <section class="ren-decoration-cp ren-decoration-cp-card-list" :class="layoutClass" :style="gridStyle">
    <div v-if="dataState === 'loading'" class="ren-decoration-cp-card-list__state">加载中...</div>
    <div v-else-if="dataState === 'error'" class="ren-decoration-cp-card-list__state">{{ errorText }}</div>
    <div v-else-if="items.length === 0" class="ren-decoration-cp-card-list__state">{{ emptyText }}</div>
    <article v-for="(item, index) in items" v-else :key="itemKey(item, index)" :style="cardStyle">
      <img v-if="itemImage(item)" :src="itemImage(item)" :alt="itemTitle(item)" />
      <div>
        <strong>{{ itemTitle(item) }}</strong>
        <span>{{ itemSubtitle(item) }}</span>
      </div>
    </article>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  numberValue,
  previewBoundString,
  previewDataRecord,
  previewRecordsFor,
  recordList,
  stringValue
} from '../../../previews/preview-helpers'
import { widgetMediaUrl } from '../../media'
import type { WidgetPreviewProps } from '../../types'

const props = defineProps<WidgetPreviewProps>()

const layoutMode = computed(() => String(props.component.props?.layoutMode ?? 'GRID').toUpperCase())
const layoutClass = computed(() => (layoutMode.value === 'WATERFALL' ? 'is-waterfall' : 'is-grid'))
const columns = computed(() => Math.max(1, Math.min(4, Math.round(numberValue(props.component.props?.columns, 2)))))
const dataRecord = computed(() => previewDataRecord(props.componentData))
const dataState = computed(() => stringValue(dataRecord.value.status).toLowerCase())
const errorText = computed(() => stringValue(dataRecord.value.error, '加载失败'))
const emptyText = computed(() => stringValue(props.component.props?.emptyText, '暂无数据'))
const pageSize = computed(() => {
  const value = Number(props.component.props?.pageSize)
  return Number.isFinite(value) && value > 0 ? Math.min(12, Math.round(value)) : 6
})
const items = computed(() =>
  previewRecordsFor(props.component, props.componentData, recordList(props.component.props?.items)).slice(
    0,
    pageSize.value
  )
)
const gridStyle = computed(() => {
  const gap = Number(props.component.style?.gap)
  const gapValue = Number.isFinite(gap) && gap >= 0 ? `${gap}px` : undefined
  if (layoutMode.value === 'WATERFALL') {
    return { columnCount: columns.value, columnGap: gapValue }
  }
  return {
    gridTemplateColumns: `repeat(${columns.value}, minmax(0, 1fr))`,
    gap: gapValue
  }
})
const cardStyle = computed(() => {
  const radius = Number(props.component.style?.radius)
  const color = stringValue(props.component.style?.cardColor)
  return {
    backgroundColor: color || undefined,
    borderRadius: Number.isFinite(radius) && radius >= 0 ? `${radius}px` : undefined
  }
})

function itemTitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.title',
    stringValue(item.title ?? item.label ?? item.name, '卡片内容'),
    item
  )
}

function itemSubtitle(item: Record<string, unknown>) {
  return previewBoundString(
    props.component,
    props.componentData,
    'item.subtitle',
    stringValue(item.subtitle ?? item.summary),
    item
  )
}

function itemImage(item: Record<string, unknown>) {
  return widgetMediaUrl(
    previewBoundString(
      props.component,
      props.componentData,
      'item.imageUrl',
      stringValue(item.imageUrl ?? item.coverUrl),
      item
    )
  )
}

function itemKey(item: Record<string, unknown>, index: number) {
  return stringValue(item.id ?? item.code, `${itemTitle(item)}-${index}`)
}
</script>

<style scoped>
.ren-decoration-cp-card-list {
  min-height: 96px;
}

.ren-decoration-cp-card-list.is-grid {
  display: grid;
  gap: 8px;
}

.ren-decoration-cp-card-list.is-waterfall article {
  break-inside: avoid;
  margin-bottom: 8px;
}

.ren-decoration-cp-card-list article {
  display: grid;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--ren-theme-border);
  background: var(--ren-theme-card);
}

.ren-decoration-cp-card-list img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
}

.ren-decoration-cp-card-list article div {
  display: grid;
  gap: 4px;
  padding: 8px;
}

.ren-decoration-cp-card-list span {
  color: var(--ren-theme-muted);
  font-size: 12px;
  overflow-wrap: anywhere;
}

.ren-decoration-cp-card-list strong {
  overflow-wrap: anywhere;
}

.ren-decoration-cp-card-list__state {
  grid-column: 1 / -1;
  min-height: 96px;
  display: grid;
  place-items: center;
  color: var(--ren-theme-muted);
  background: var(--ren-theme-background);
}
</style>
